import React from 'react';
import { Button } from '../components/ui/button';
import { Heart, Target, Users, Shield } from 'lucide-react';
import SEO from '../components/SEO';

const About = () => {
  const appUrl = process.env.REACT_APP_EXTERNAL_APP_URL || 'https://aupairshare.base44.app';

  const handleAppRedirect = () => {
    window.open(appUrl, '_blank');
  };

  const values = [
    {
      icon: <Heart className="w-10 h-10" />,
      title: 'Family First',
      description: 'Every decision we make prioritizes the wellbeing and convenience of families and children.'
    },
    {
      icon: <Shield className="w-10 h-10" />,
      title: 'Safety Always',
      description: 'No compromises on vetting, verification, and accountability measures.'
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: 'Community Support',
      description: 'Building a trusted village of families and au pairs who support each other.'
    },
    {
      icon: <Target className="w-10 h-10" />,
      title: 'Flexibility',
      description: 'Childcare that adapts to your life, not the other way around.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEO 
        title="About Us"
        description="Au Pair Share was founded by a psychologist mom who understood the challenges of finding reliable childcare. Learn about our mission to be your village."
        keywords="about au pair share, childcare company, South Africa childcare, founder story"
        path="/about"
      />
      {/* Hero */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-100 to-slate-50 opacity-50"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
              About Au Pair Share
            </h1>
            <p className="text-2xl text-slate-600 leading-relaxed">
              Let us be your village.
            </p>
          </div>
        </div>
      </section>

      {/* The Story */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-8">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">
                Why Au Pair Share Exists
              </h2>
              <div className="prose prose-lg text-slate-600 space-y-4">
                <div className="bg-slate-50 border-l-4 border-slate-700 rounded-r-lg p-6 mb-6">
                  <p className="text-lg leading-relaxed italic text-slate-700">
                    Au Pair Share was birthed by a mom who is a psychologist—someone who deeply understands child development and family dynamics, but who also experienced firsthand the frustration of struggling with traditional au pair arrangements.
                  </p>
                  <p className="text-lg leading-relaxed italic text-slate-700 mt-3">
                    She knew there had to be a better way: quality care without the complexity, flexibility without compromising safety, and affordability without sacrificing reliability.
                  </p>
                </div>
                <p className="text-lg leading-relaxed">
                  Modern parents face an impossible challenge: balancing demanding careers with quality childcare. Full-time au pairs are expensive and often unnecessary. Grandparents live far away. Traditional agencies are rigid and costly. And e-hailing services — while convenient — are not designed for children.
                </p>
                <p className="text-lg leading-relaxed">
                  Au Pair Share was born from a simple truth: <span className="font-semibold text-slate-900">families need flexible, trusted childcare without the full-time commitment</span>.
                </p>
                <p className="text-lg leading-relaxed">
                  We connect vetted students and qualified childcare professionals with families who need part-time, same-day, or emergency support. It is not a job for our au pairs — it is a community. And it is not just a service for families — it is their village.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem We Solve */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-slate-900 mb-12 text-center">
              The Problem We Solve
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-2xl font-semibold text-slate-900 mb-4">For Families</h3>
                <ul className="space-y-3 text-slate-600">
                  <li className="flex items-start gap-2">
                    <span className="text-slate-400 mt-1">→</span>
                    <span>Can't afford full-time au pairs but need regular help</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-slate-400 mt-1">→</span>
                    <span>Last-minute schedule changes with no backup plan</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-slate-400 mt-1">→</span>
                    <span>Don't trust e-hailing services with their children</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-slate-400 mt-1">→</span>
                    <span>Want childcare, not just transportation</span>
                  </li>
                </ul>
              </div>
              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-2xl font-semibold text-slate-900 mb-4">For Au Pairs</h3>
                <ul className="space-y-3 text-slate-600">
                  <li className="flex items-start gap-2">
                    <span className="text-slate-400 mt-1">→</span>
                    <span>Need flexible work around studies or other commitments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-slate-400 mt-1">→</span>
                    <span>Want reliable income without fixed commitments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-slate-400 mt-1">→</span>
                    <span>Looking for meaningful, responsible work experience</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-slate-400 mt-1">→</span>
                    <span>Want to build relationships with multiple families</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Our Commitment
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {values.map((value, index) => (
              <div key={index} className="text-center space-y-4">
                <div className="bg-slate-100 w-16 h-16 rounded-xl flex items-center justify-center mx-auto text-slate-700">
                  {value.icon}
                </div>
                <h3 className="text-xl font-semibold text-slate-900">{value.title}</h3>
                <p className="text-slate-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-4xl lg:text-5xl font-bold">
              Our Mission
            </h2>
            <p className="text-2xl text-slate-300 leading-relaxed">
              To create a trusted community where families find flexible, quality childcare and au pairs earn meaningful income — all built on safety, transparency, and genuine care.
            </p>
            <div className="pt-8">
              <Button 
                size="lg"
                onClick={handleAppRedirect}
                className="bg-white text-slate-900 hover:bg-slate-100 px-8 py-6 text-lg"
              >
                Join Our Community
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-4xl font-bold text-slate-900">
              Serving Major Cities Across South Africa
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Au Pair Share is proudly South African, serving families in Johannesburg, Pretoria, Cape Town, and Durban with locally vetted au pairs who understand our communities and neighborhoods.
            </p>
            <p className="text-lg text-slate-600">
              We are building something better — one family, one au pair, one booking at a time.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto space-y-6">
            <h2 className="text-4xl font-bold text-slate-900">
              Ready to Experience the Difference?
            </h2>
            <p className="text-xl text-slate-600">
              Join Au Pair Share today and discover childcare that truly fits your life.
            </p>
            <Button 
              size="lg"
              onClick={handleAppRedirect}
              className="bg-slate-700 hover:bg-slate-800 text-white px-8 py-6 text-lg mt-4"
            >
              Open the App
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
